import React, { useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { brands } from '../data/brands';
import { services } from '../data/services';
import { serviceTypes } from '../data/serviceTypes';
import { brandServiceKeywords } from '../data/brandServiceKeywords';
import { capitalizeWords } from '../utils/stringUtils';
import LocationSelector from '../components/booking/LocationSelector';
import ServiceTypesList from '../components/brands/ServiceTypesList';
import ContactCTA from '../components/shared/ContactCTA';

export default function BrandServiceDetail() {
  const navigate = useNavigate();
  const { brand: brandId, service: serviceId } = useParams();
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  
  const brand = brands.find(b => b.id === brandId);
  const service = services.find(s => s.id === serviceId);
  const brandKeywords = brandServiceKeywords[brandId || '']?.[serviceId || ''] || [];

  if (!brand || !service) {
    return <Navigate to="/brands" replace />;
  }

  const handleLocationSelect = () => {
    if (!selectedState) return;

    // Build the URL based on available location data
    let path = `/brands/${brandId}/${serviceId}/${selectedState}`;
    if (selectedCity) {
      path += `/${selectedCity}`;
      if (selectedArea) {
        path += `/${selectedArea}`;
      }
    }
    navigate(path);
  };

  return (
    <>
      <Helmet>
        <title>{`${brand.name} ${service.name} Service Center | Parakh Services`}</title>
        <meta 
          name="description" 
          content={`Professional ${brand.name} ${service.name.toLowerCase()} repair service center including ${brandKeywords.slice(0, 5).join(', ')}. Expert technicians and same-day service with warranty.`} 
        />
      </Helmet>

      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {brand.name} {service.name} Service Center
            </h1>
            <p className="text-xl text-gray-600">
              Professional repair service center for all {brand.name} models
            </p>

            {/* Keywords Section */}
            {brandKeywords.length > 0 && (
              <div className="mt-8">
                <div className="flex flex-wrap justify-center gap-2">
                  {brandKeywords.map((keyword, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Location Selection */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Find Service Center Near You
              </h2>
              <LocationSelector
                state={selectedState}
                city={selectedCity}
                area={selectedArea}
                onStateChange={setSelectedState}
                onCityChange={setSelectedCity}
                onAreaChange={setSelectedArea}
              />
              <button
                onClick={handleLocationSelect}
                disabled={!selectedState}
                className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                Find Service Center
              </button>
            </div>
          </div>

          {/* Service Types List */}
          <ServiceTypesList
            service={service}
            brandName={brand.name}
            location={
              selectedState ? {
                state: capitalizeWords(selectedState.replace(/-/g, ' ')),
                city: selectedCity ? capitalizeWords(selectedCity.replace(/-/g, ' ')) : undefined,
                area: selectedArea ? capitalizeWords(selectedArea.replace(/-/g, ' ')) : undefined
              } : undefined
            }
          />

          <ContactCTA />
        </div>
      </div>
    </>
  );
}